'use client';

import Image from 'next/image';
import { Icon, IconName } from '~/components/core/Icon';
import { categoryValues, getSanitizedCategoryName } from '~/utils/storeUtils';
import { spacing, colors } from '~/utils/tailwindUtils';
import { ProductCategory } from '~/utils/claimUtils';
import { twMerge } from 'tailwind-merge';
import Flex from '~/components/customComponents/flex/Flex';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColorModeEnum } from '~/utils/generalUtils';

export const ProductImage = ({
  imageUrl,
  productName,
  productCategory,
  size = 'sm',
  className = '',
  isTransparent = false,
}: {
  imageUrl?: string;
  productName?: string;
  productCategory?: ProductCategory | null;
  size?: 'sm' | 'lg'; // TODO: Replace other instances with this one and Prop large
  className?: string;
  isTransparent?: boolean;
}) => {
  const imageLoader = useCallback(({ src, width, quality }: any) => {
    return `${src}?w=${width}&q=${quality || 75}`;
  }, []);

  const [isImageError, setIsImageError] = useState(false);

  // Memoize decoded URL to prevent unnecessary recalculations
  const decodedImageUrl = useMemo(() => {
    if (imageUrl && imageUrl !== 'null') {
      try {
        return decodeURIComponent(imageUrl);
      } catch {
        return null;
      }
    }
    return null;
  }, [imageUrl]);

  // Reset error state when imageUrl changes
  useEffect(() => {
    setIsImageError(false);
  }, [imageUrl]);

  const handleImageError = useCallback(() => {
    setIsImageError(true);
  }, []);

  const productCategoryName = useMemo(
    () => getSanitizedCategoryName(productCategory),
    [productCategory],
  );

  // Memoize icon name to prevent unnecessary recalculations
  const iconName = useMemo(
    () => categoryValues[productCategoryName as keyof typeof categoryValues]?.icon,
    [productCategoryName],
  ) as IconName;

  const colorMode = useMemo(
    () => categoryValues[productCategoryName as keyof typeof categoryValues]?.colorMode,
    [productCategoryName],
  ) as ColorModeEnum;

  const strokeWidth = useMemo(
    () => categoryValues[productCategoryName as keyof typeof categoryValues]?.strokeWidth,
    [productCategoryName],
  ) as number;

  const containerClassName = twMerge(
    'relative aspect-square',
    size === 'sm' ? 'h-8 w-8' : 'h-40 w-40',
    className,
  );

  if (!isImageError && decodedImageUrl) {
    return (
      <Flex className={containerClassName}>
        <Flex className="absolute inset-3">
          <Image
            src={decodedImageUrl}
            alt={productName ?? ''}
            fill
            sizes={size === 'sm' ? '32px' : '240px'}
            className={twMerge('object-cover', isTransparent ? 'mix-blend-darken' : '')}
            loader={imageLoader}
            onError={handleImageError}
            priority={false}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex className={twMerge(containerClassName, 'justify-center')}>
      <Icon
        name={iconName || IconName.Shop}
        size={size === 'sm' ? spacing[5] : spacing[12]}
        color={colors.gray[400]}
        colorMode={colorMode}
        customStrokeWidth={strokeWidth}
      />
    </Flex>
  );
};
export default ProductImage;
